import { useCallback } from 'react';
import { Routes, Route } from 'react-router-dom';
import { useMsal, useIsAuthenticated, AuthenticatedTemplate, UnauthenticatedTemplate } from '@azure/msal-react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { Shell } from './components/Shell.js';
import { CommandCentre } from './pages/CommandCentre.js';
import { Integrations } from './pages/Integrations.js';
import { Placeholder } from './pages/Placeholder.js';
import { Campaigns } from './pages/Campaigns.js';
import { Keywords } from './pages/Keywords.js';
import { Budget } from './pages/Budget.js';
import { Audit } from './pages/Audit.js';
import { LandingPages } from './pages/LandingPages.js';
import { Settings } from './pages/Settings.js';
import { Analyst } from './pages/Analyst.js';
import { SearchTerms } from './pages/SearchTerms.js';
import { Geography } from './pages/Geography.js';
import { Conversions } from './pages/Conversions.js';
import { Recommendations } from './pages/Recommendations.js';
import { BUILT_PATHS, NAV_ITEMS } from './navigation.js';
import { loginRequest } from './authConfig.js';
import { useTheme } from './lib/theme.js';

/** Screens with a real implementation. Everything else still routes to the placeholder. */

export function App(): JSX.Element {
  const { instance, accounts } = useMsal();
  // Applies the stored theme to <html> on mount, for the sign-in view too.
  const { theme } = useTheme();
  const isAuthenticated = useIsAuthenticated();
  const account = accounts[0];

  const getToken = useCallback(async (): Promise<string | null> => {
    if (!account) return null;
    try {
      const result = await instance.acquireTokenSilent({ ...loginRequest, account });
      return result.accessToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        const result = await instance.acquireTokenPopup(loginRequest);
        return result.accessToken;
      }
      return null;
    }
  }, [instance, account]);

  const roles = ((account?.idTokenClaims as { roles?: string[] } | undefined)?.roles ?? []) as string[];

  return (
    <>
      <UnauthenticatedTemplate>
        <div className="signin">
          <div className="signin-card">
            {/* The sign-in card is a light surface in light mode, so it takes
                the published purple logo; the reversed one is for dark. */}
            <img
              className="signin-logo"
              src={theme === 'light' ? '/brand/logo.svg' : '/brand/logo-reversed.svg'}
              alt="TwentyFour IT Services"
            />
            <h1>24IT Growth Engine</h1>
            <p>Advertising and marketing intelligence for TwentyFour IT Services</p>
            <button className="btn" onClick={() => void instance.loginRedirect(loginRequest)}>
              Sign in with Microsoft
            </button>
          </div>
        </div>
      </UnauthenticatedTemplate>

      <AuthenticatedTemplate>
        {isAuthenticated ? (
          <Routes>
            <Route
              element={
                <Shell
                  userName={account?.name ?? account?.username ?? 'Unknown user'}
                  roles={roles}
                  onSignOut={() => void instance.logoutRedirect()}
                />
              }
            >
              <Route path="/" element={<CommandCentre getToken={getToken} />} />
              <Route path="/integrations" element={<Integrations getToken={getToken} />} />
              <Route path="/campaigns" element={<Campaigns getToken={getToken} />} />
              <Route path="/keywords" element={<Keywords getToken={getToken} />} />
              <Route path="/budget" element={<Budget getToken={getToken} />} />
              <Route path="/audit" element={<Audit getToken={getToken} />} />
              <Route path="/landing-pages" element={<LandingPages getToken={getToken} />} />
              <Route path="/settings" element={<Settings getToken={getToken} />} />
              <Route path="/analyst" element={<Analyst getToken={getToken} />} />
              <Route path="/search-terms" element={<SearchTerms getToken={getToken} />} />
              <Route path="/geography" element={<Geography getToken={getToken} />} />
              <Route path="/conversions" element={<Conversions getToken={getToken} />} />
              <Route path="/recommendations" element={<Recommendations getToken={getToken} />} />
              {NAV_ITEMS.filter((i) => !BUILT_PATHS.has(i.path)).map((i) => (
                <Route key={i.path} path={i.path} element={<Placeholder />} />
              ))}
              <Route path="*" element={<Placeholder />} />
            </Route>
          </Routes>
        ) : null}
      </AuthenticatedTemplate>
    </>
  );
}
